import { Controller } from "@hotwired/stimulus"

// 이메일 중복 확인 컨트롤러
// - [중복확인] 버튼 클릭 시에만 형식 검증 + 중복 확인 API 호출.
//   (blur 형식검증은 없앴다 — 버튼 클릭과 경쟁해 "형식검증만 되고 중복확인은 안 되는 것처럼"
//    보이던 문제를 제거하기 위함. 형식 오류는 중복확인 클릭 시 안내한다.)
// - registration-form 컨트롤러에 상태 변경 알림
export default class extends Controller {
  static targets = [
    "input",
    "checkButton",
    "invalidMessage",
    "availableMessage",
    "duplicateMessage",
    "loadingSpinner"
  ]
  static outlets = ["registration-form"]

  connect() {
    this.hideAllMessages()
    this.isChecking = false
    this.isAvailable = false
  }

  // registration-form 컨트롤러에 상태 변경 알림
  notifyFormController() {
    if (this.hasRegistrationFormOutlet) {
      this.registrationFormOutlet.emailValidationChanged()
    }
  }

  // 입력이 바뀌면 이전 중복확인 결과·표시를 초기화
  onInput() {
    this.isAvailable = false
    this.hideAllMessages()
    this.resetInputStyle()
    this.enableCheckButton()
    this.notifyFormController()
  }

  // [중복확인] 클릭 — 형식 검증 후 API 호출. (형식 검증은 이 경로에서만 수행)
  async checkDuplicate() {
    const email = this.inputTarget.value.trim()

    if (!email) {
      return
    }

    if (!this.isValidEmail(email)) {
      this.showInvalidFormat()
      return
    }

    if (this.isChecking) {
      return
    }

    this.isChecking = true
    this.hideAllMessages()
    this.resetInputStyle()
    this.showLoading()

    try {
      const response = await fetch('/api/v1/accountability/check-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ login_id: email })
      })

      const data = await response.json()

      if (data.success) {
        this.isAvailable = true
        this.showAvailable()
        this.showResultAlert('사용 가능한 이메일입니다.')
      } else {
        this.isAvailable = false
        const msg = data.error?.message || '이미 사용 중인 이메일입니다.'
        this.showDuplicate()
        this.showResultAlert(msg)
      }
    } catch (error) {
      console.error('이메일 중복 확인 오류:', error)
      // 네트워크 오류는 '결과 확정'이 아니라 재시도 대상 — 오해 소지 있는 인라인 문구는 띄우지 않고
      // 붉은 테두리 + 모달 안내만.
      this.isAvailable = false
      const errMsg = '중복 확인 중 오류가 발생했습니다.'
      this.markEmailUnavailable()
      this.showResultAlert(errMsg)
    } finally {
      this.isChecking = false
      this.hideLoading()
      this.notifyFormController()
    }
  }

  // components/basics/alert 컴포넌트를 dispatch 이벤트로 열기 — worker_complaint 와 동일 패턴.
  showResultAlert(message) {
    const alertEl = document.getElementById('email-check-result-alert')
    if (!alertEl) return
    const msgEl = alertEl.querySelector("[data-alert-target='body'] p")
    if (msgEl) msgEl.textContent = message
    alertEl.dispatchEvent(new CustomEvent('alert:open'))
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  hideAllMessages() {
    this.invalidMessageTarget.classList.add("hidden")
    this.availableMessageTarget.classList.add("hidden")
    this.duplicateMessageTarget.classList.add("hidden")
  }

  resetInputStyle() {
    this.inputTarget.classList.remove("border-success-500", "border-danger-500")
  }

  showInvalidFormat() {
    this.hideAllMessages()
    this.invalidMessageTarget.classList.remove("hidden")
    this.inputTarget.classList.remove("border-success-500")
    this.inputTarget.classList.add("border-danger-500")
  }

  showAvailable() {
    this.hideAllMessages()
    this.availableMessageTarget.classList.remove("hidden")
    this.inputTarget.classList.remove("border-danger-500")
    this.inputTarget.classList.add("border-success-500")
    this.disableCheckButton()
  }

  // 중복(이미 사용 중) — 붉은 테두리 + 인라인 "이미 사용 중인 이메일입니다" 문구.
  // 모달을 닫아도 상태가 남아 사용자가 인지할 수 있게 인라인 메시지도 함께 노출한다.
  showDuplicate() {
    this.hideAllMessages()
    this.duplicateMessageTarget.classList.remove("hidden")
    this.inputTarget.classList.remove("border-success-500")
    this.inputTarget.classList.add("border-danger-500")
  }

  // 네트워크 오류 등 결과 미확정 실패 — 인라인 문구 없이 border 만 (모달로 안내).
  markEmailUnavailable() {
    this.hideAllMessages()
    this.inputTarget.classList.remove("border-success-500")
    this.inputTarget.classList.add("border-danger-500")
  }

  showLoading() {
    this.checkButtonTarget.disabled = true
    this.loadingSpinnerTarget.classList.remove("hidden")
    this.checkButtonTarget.querySelector('.button-text').classList.add("opacity-0")
  }

  hideLoading() {
    // 중복 확인 성공 시에는 버튼을 비활성화 상태로 유지
    if (!this.isAvailable) {
      this.checkButtonTarget.disabled = false
    }
    this.loadingSpinnerTarget.classList.add("hidden")
    this.checkButtonTarget.querySelector('.button-text').classList.remove("opacity-0")
  }

  enableCheckButton() {
    this.checkButtonTarget.disabled = false
  }

  disableCheckButton() {
    this.checkButtonTarget.disabled = true
  }
}
